import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Package, RotateCcw, Shield, Clock, AlertTriangle, XCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PoliciesPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium">Policies</span>
          </nav>

          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Policies & Procedures
            </h1>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Everything you need to know about our shipping, returns, and order policies.
            </p>
          </div>

          <div className="space-y-6">
            {/* Important Notice */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-6 md:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="font-serif text-xl font-semibold text-red-800 mb-3">
                    Important: Please Read Before Ordering
                  </h2>
                  <div className="space-y-3 text-red-700">
                    <p className="flex items-start gap-2">
                      <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span><strong>No returns once a proof is approved.</strong> Please review your proof carefully before approval.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span><strong>No refunds.</strong> If you are unsatisfied, your funds can be applied as store credit toward another order.</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span><strong>No free replacements.</strong> Replacement orders are available at standard pricing.</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Policy */}
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-amber-600" />
                </div>
                <h2 className="font-serif text-xl md:text-2xl font-semibold text-gray-900">
                  Shipping Policy
                </h2>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Standard shipping takes 5-7 business days</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Express shipping available (2-3 business days)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Free shipping on orders over $75</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">All orders are processed within 1-2 business days</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Tracking information provided via email</span>
                </li>
              </ul>
            </div>

            {/* Returns & Refunds Policy */}
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm border-2 border-amber-200">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                  <RotateCcw className="w-6 h-6 text-amber-600" />
                </div>
                <h2 className="font-serif text-xl md:text-2xl font-semibold text-gray-900">
                  Returns & Refunds Policy
                </h2>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600"><strong>No returns accepted once a proof has been approved.</strong> Your approval confirms the design, placement, and specifications are correct.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600"><strong>All sales are final – no refunds will be issued.</strong> We encourage you to review all details carefully before approving.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600"><strong>Store Credit Option:</strong> If you are unsatisfied with your order, your payment can be applied as store credit toward a future order.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Please contact us within 7 days of receiving your order to discuss store credit options.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Damaged items during shipping must be reported within 48 hours with photos for review.</span>
                </li>
              </ul>
            </div>

            {/* Quality & Replacements */}
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-amber-600" />
                </div>
                <h2 className="font-serif text-xl md:text-2xl font-semibold text-gray-900">
                  Quality & Replacements
                </h2>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">All prints are hand-inspected before shipping to ensure quality</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Premium materials used for all products</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Vibrant, long-lasting colors on all garments</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600"><strong>No free replacements.</strong> If you need a replacement order, it will be charged at standard pricing.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">For production errors on our end (wrong size shipped, print defect not matching approved proof), contact us immediately for resolution.</span>
                </li>
              </ul>
            </div>

            {/* Custom Orders */}
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <h2 className="font-serif text-xl md:text-2xl font-semibold text-gray-900">
                  Custom Orders & Proofs
                </h2>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Custom orders require 7-14 business days for production</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600"><strong>Design proofs will be provided for your approval before production begins.</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600"><strong>Once you approve a proof, the order is final and cannot be changed, returned, or refunded.</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Please review spelling, sizing, colors, and placement carefully before approving</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Bulk order discounts available – see our <Link to="/bulk-orders" className="text-amber-600 hover:underline">Bulk Orders page</Link> for pricing tiers</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-600">Minimum quantity may apply for custom designs</span>
                </li>
              </ul>
            </div>

            {/* Store Credit Info */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 md:p-8">
              <h3 className="font-serif text-xl font-semibold text-gray-900 mb-4">
                About Store Credit
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Store credit is issued at our discretion and must be requested within 7 days of receiving your order</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Store credit can be applied to any future order including custom and bulk orders</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Store credit is non-transferable and has no cash value</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                  <span>Store credit does not expire</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-amber-50 to-amber-100 rounded-xl p-6 md:p-8 text-center">
            <h3 className="font-serif text-xl font-semibold text-gray-900 mb-2">
              Have Questions?
            </h3>
            <p className="text-gray-600 mb-4">
              We're here to help! Contact us at{' '}
              <a href="mailto:aj@adroyalink.com" className="text-amber-600 hover:underline">
                aj@adroyalink.com
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PoliciesPage;
